import { WindowLocation } from "@reach/router";
import { graphql } from "gatsby";
import React from "react";
import Card from "../components/Card/Card";
import EditorEmbedSupport from "../components/EditorEmbedSupport/EditorEmbedSupport";
import UnitOfWorkLayout from "../components/UnitOfWorkLayout/UnitOfWorkLayout";
import PageLayout from "../layouts/PageLayout/PageLayout";
import { CommonContext, SanityCampaign, SanityPage } from "../model/common";
import { SanityLesson, SanityUnitOfWork } from "../model/lessons";
import { GlobalMenus } from "../model/menu";
import { SiteArea } from "../utils/analytics";
import { LessonDetails } from "../utils/LessonUtils";
import styles from "./LessonPage.module.scss";

export const pageQuery = graphql`
  query LessonPage($_id: String!, $unitId: String!, $navLanguage: String) {
    menus: sanityGlobalConfig(language: { eq: $navLanguage }) {
      ...MenuData
    }
    listings: sanityUnitOfWorkListingPage {
      _id
      _type
      title
    }
    lesson: sanityLesson(_id: { eq: $_id }) {
      _id
      _type
      title
      slug {
        current
      }
      _rawIntroduction(resolveReferences: { maxDepth: 5 })
      _rawSummary(resolveReferences: { maxDepth: 5 })
      _rawLearningObjectives(resolveReferences: { maxDepth: 5 })
      ...LessonTopics
      completedCode {
        _key
        name
        editors
        scratch {
          url
          file {
            asset {
              originalFilename
              extension
              path
            }
          }
          scratchImages {
            asset {
              ...SanityImageAsset
            }
          }
        }
        makecode {
          project
          url
        }
        python {
          main
        }
      }
      image {
        ...SanityImage
      }
      downloads {
        _key
        name
        category
        file {
          asset {
            originalFilename
            extension
            path
          }
        }
        pdf {
          asset {
            originalFilename
            extension
            path
          }
        }
      }
      metaDescription
      socialTitle
      socialDescription
      socialImage {
        ...SanityImage
      }
      fields {
        editorLinks
      }
    }
    unit: sanityUnitOfWork(_id: { eq: $unitId }) {
      ...SanityUnitOfWorkForListing
      contents {
        downloads {
          category
          name
        }
      }
      metaDescription
      socialTitle
      socialDescription
      socialImage {
        ...SanityImage
      }
      contents {
        _id
        _type
        title
        slug {
          current
        }
      }
      ...SanityUnitOfWorkRelatedField
      sponsor {
        ...ExternalCollaborator
      }
      licence {
        ...Licence
      }
    }
  }
`;

const LessonPage = ({
  pageContext,
  data: { menus, unit, lesson, listings },
  location
}: LessonPageProps) => {
  const { title } = lesson;
  return (
    <EditorEmbedSupport document={lesson}>
      <PageLayout
        siteArea={SiteArea.LESSONS}
        metadata={{
          title,
          alternates: pageContext.alternates,
          page: lesson
        }}
        menus={menus}
        messages={pageContext.messages}
        location={location}
      >
        <UnitOfWorkLayout
          listings={listings}
          unit={unit}
          lesson={lesson}
          details={
            <Card type="main">
              <LessonDetails
                unit={unit}
                lesson={lesson}
                downloadClassName={styles.downloads}
              />
            </Card>
          }
        />
      </PageLayout>
    </EditorEmbedSupport>
  );
};

export default LessonPage;

interface LessonPageProps {
  data: {
    menus: GlobalMenus;
    unit: SanityUnitOfWork;
    lesson: SanityLesson;
    listings: SanityPage;
    campaigns: { nodes: SanityCampaign[] };
  };
  location: WindowLocation;
  pageContext: CommonContext & { unitId: string };
}
